import React, { useState, useRef } from 'react';
import { useMediaAttachment } from '../../../hooks/useMediaAttachment';

export const AudioAttachment = ({ mediaId }: { mediaId: string }) => {
    const { url, status, downloadMedia } = useMediaAttachment(mediaId);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState("0:00");
    const audioRef = useRef<HTMLAudioElement>(null);

    // Array de 40 barras para un waveform largo y estético
    const waveformHeights = [
        8, 12, 10, 18, 24, 14, 8, 20, 28, 22, 16, 10, 24, 18, 12, 8, 14, 20, 26, 12,
        10, 16, 14, 20, 24, 12, 8, 18, 26, 20, 14, 10, 22, 16, 12, 8, 12, 18, 24, 10
    ];

    // Función para formatear segundos a M:SS
    const formatTime = (time: number) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const current = audioRef.current.currentTime;
            const duration = audioRef.current.duration;
            setProgress((current / duration) * 100);
            setCurrentTime(formatTime(current));
        }
    };

    const onLoadedMetadata = () => {
        if (audioRef.current) {
            setCurrentTime(formatTime(audioRef.current.duration));
        }
    };

    return (
        <div className="audio-attachment-wrapper" style={{ minWidth: '220px', maxWidth: '100%' }}>
            <div className="d-flex align-items-center gap-1 gap-sm-2">

                {/* Botón de Reproducción */}
                <div className="flex-shrink-0">
                    {status === 'LOADING' ? (
                        <div className="spinner-border spinner-border-sm text-success" />
                    ) : (
                        <button
                            className="btn btn-link p-0 border-0 d-flex align-items-center"
                            onClick={status === 'SUCCESS' ? togglePlay : downloadMedia}
                        >
                            <i className={`ri-${isPlaying ? 'pause' : 'play'}-fill`}
                               style={{ fontSize: 'clamp(32px, 5vw, 40px)', color: '#667781' }}>
                            </i>
                        </button>
                    )}
                </div>

                {/* Contenedor Central: Waveform + Tiempo (Responsivo) */}
                <div className="flex-grow-1 d-flex flex-column justify-content-center mt-3" style={{ minWidth: 0 }}>
                    {/* Waveform Graphic con scroll oculto para móviles */}
                    <div className="d-flex align-items-center gap-1"
                         style={{ height: '30px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {waveformHeights.map((h, i) => {
                            const barProgress = (i / waveformHeights.length) * 100;
                            const isPlayed = progress > barProgress;

                            return (
                                <div key={i} className="position-relative d-flex align-items-center flex-shrink-0">
                                    {/* Punto verde indicador de posición */}
                                    {isPlayed && progress <= ((i + 1) / waveformHeights.length) * 100 && (
                                        <div className="position-absolute rounded-circle"
                                             style={{ width: '8px', height: '8px', background: '#25D366', left: '-2px', zIndex: 2 }} />
                                    )}
                                    {/* Barra de la onda */}
                                    <div
                                        style={{
                                            width: '2px',
                                            height: `${h}px`,
                                            backgroundColor: isPlayed ? '#25D366' : '#d1d7db',
                                            borderRadius: '4px',
                                            transition: 'background-color 0.1s'
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {/* Tiempo de reproducción/duración alineado a la izquierda */}
                    <span style={{
                        fontSize: '11px',
                        color: '#667781',
                        alignSelf: 'flex-start',
                        marginTop: '2px'
                    }}>
                        {currentTime}
                    </span>
                </div>

                {/* Avatar de Perfil con Icono de Micrófono */}
                <div className="position-relative flex-shrink-0 ms-1 ms-sm-2">
                    <div className="rounded-circle border d-flex align-items-center justify-content-center overflow-hidden bg-light shadow-sm"
                         style={{ width: 'clamp(38px, 8vw, 48px)', height: 'clamp(38px, 8vw, 48px)' }}>
                        <i className="ri-user-3-line text-muted" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}></i>
                    </div>
                    {/* Icono de micrófono verde superpuesto */}
                    <div className="position-absolute bg-white rounded-circle d-flex align-items-center justify-content-center border"
                         style={{
                             bottom: '0px',
                             left: '-5px',
                             width: '20px',
                             height: '20px',
                             boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                         }}>
                        <i className="ri-mic-fill text-success" style={{ fontSize: '12px' }}></i>
                    </div>
                </div>
            </div>

            {/* Elemento de Audio Oculto */}
            {url && (
                <audio
                    ref={audioRef}
                    src={url}
                    onLoadedMetadata={onLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={() => { setIsPlaying(false); setProgress(0); }}
                    className="d-none"
                />
            )}
        </div>
    );
};
